import { Outlet, useLocation } from 'react-router';
import { useEffect } from 'react';
import { AnnouncementBar } from '@/components/AnnouncementBar';
import { Header } from '@/components/Header';
import { MobileNavigation } from '@/components/MobileNavigation';
import { Footer } from '@/components/Footer';
import { CartDrawer } from '@/features/cart/CartDrawer';
import { CookieConsent } from '@/components/CookieConsent';
import { track } from '@/client/lib/analytics';
import { useI18n } from '@/client/i18n/I18nContext';
import { useConsent } from '@/client/consent/ConsentContext';
import { INITIAL_LOCALE } from '@/client/i18n/locale';
import { toLogicalPath } from '@/lib/seo';
import { isEditFilterNav } from '@/pages/TheEditPage';

export function RootLayout() {
  const location = useLocation();
  const { t } = useI18n();
  const { analytics } = useConsent();

  // A new page starts at the top, except an Edit category filter: TheEditPage
  // brings its filter row to the top of the viewport itself (EDIT_FILTER_STATE).
  useEffect(() => {
    if (!isEditFilterNav(location.state)) window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Page views. GA loads only after consent and is configured with
  // `send_page_view: false`, so we emit page_view ourselves. This component
  // mounts (and this effect first runs) BEFORE ConsentProvider installs the GA
  // sink, so the entry page — often the only page a visitor sees — would go to
  // the no-op sink and never be counted. Re-emitting when `analytics` turns on
  // fixes that; on later route changes `analytics` is already true.
  // The path is the LOGICAL one (basename-relative and untranslated: /contacto
  // under /en and /fr too), so a page aggregates across languages in GA.
  useEffect(() => {
    if (analytics) track('page_view', { path: toLogicalPath(INITIAL_LOCALE, location.pathname) });
  }, [location.pathname, analytics]);

  return (
    <>
      <a className="skip-link" href="#main">
        {t('a11y.skip')}
      </a>
      <AnnouncementBar />
      <Header />
      <MobileNavigation />
      <main id="main">
        <Outlet />
      </main>
      <Footer />
      <CartDrawer />
      <CookieConsent />
    </>
  );
}
